import type { ComponentProps, ReactNode } from 'react'
import { FieldInfo, hasFieldError, useFieldContext, useFormReadOnly, useIsFieldOptional } from './shared'
import { Input } from '../../ui/input'
import { FieldLabelRow } from './field-label-row'
import { cn } from '@/lib/utils'
import { HORIZONTAL_CONTROL_CLASS, HORIZONTAL_LABEL_CLASS, HORIZONTAL_ROW_CLASS } from './horizontal-field'

export default function TextField({
  label,
  className,
  orientation,
  description,
  optional,
  ...props
}: ComponentProps<'input'> & {
  label: string
  orientation?: 'vertical' | 'horizontal'
  variant?: 'default' | 'outline' | 'background'
  description?: ReactNode
  optional?: boolean
}) {
  const field = useFieldContext<string>()
  const formReadOnly = useFormReadOnly()
  const isReadOnly = props.readOnly ?? formReadOnly
  const isOptional = useIsFieldOptional(field.name, optional)
  const isHorizontal = orientation === 'horizontal'
  const value = field.state.value ?? ''

  return (
    <div className={className}>
      <div className={isHorizontal ? HORIZONTAL_ROW_CLASS : 'grid gap-2'}>
        {label && (
          <FieldLabelRow
            label={label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
            compactOptional={isHorizontal}
            className={isHorizontal ? HORIZONTAL_LABEL_CLASS : undefined}
          />
        )}
        <div className={isHorizontal ? HORIZONTAL_CONTROL_CLASS : undefined}>
          <Input
            id={field.name}
            name={field.name}
            aria-invalid={hasFieldError(field.state.meta)}
            value={isReadOnly && !value ? '-' : value}
            onBlur={field.handleBlur}
            onChange={(e) => field.handleChange(e.target.value)}
            {...props}
            readOnly={isReadOnly}
            tabIndex={isReadOnly ? -1 : props.tabIndex}
            className={cn(
              isReadOnly &&
                'border-transparent bg-transparent px-0 focus-visible:border-transparent focus-visible:ring-0 dark:bg-transparent',
            )}
          />
          {description && <p className="text-muted-foreground m-2 text-xs">{description}</p>}
          <FieldInfo fieldState={field.state} />
        </div>
      </div>
    </div>
  )
}
